import { useState, useEffect, useRef, useContext } from 'react';
import { FaComments, FaTimes, FaPaperPlane, FaUser, FaLeaf, FaWhatsapp, FaSync, FaChalkboardTeacher, FaUserFriends, FaMoneyBillWave, FaClipboardList, FaCalendarAlt } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import HttpInterceptor from '../lib/HttpInterceptor';
import UserContext from './UserContext';

// Using a reliable avatar service for trainer chatbot - female avatar (different from Priya)
const ananyaAvatar = '/assets/ai-agents/ananya_dietitian.png';

/**
 * Trainer-Specific ChatBot Component
 * Provides assistance for trainers with business-focused FAQs and functionality
 */

// Trainer-specific FAQ data
const trainerFaqData = [
  {
    keywords: ['program', 'programs', 'create program', 'add program', 'new program', 'program management'],
    question: 'How do I create a program?',
    answer: `Creating a program is straightforward! Here's how:

1. **Navigate to Programs** - Go to your Professional Dashboard and click on "Programs" in the navigation
2. **Click "Create Program"** - You'll see a button to add a new program
3. **Fill in Details** - Add your program name, description, duration, and pricing
4. **Set Service Type** - Select the service category (Yoga, Dietitian, Gym, etc.)
5. **Save** - Once saved, your program will be visible to students in the marketplace!

Your programs are your main offerings, so make sure to include all the important details that will help students understand what they'll learn. Need help with any specific step?`,
    quickActions: [
      { text: 'Go to Programs', link: '/trainer/programs' },
      { text: 'View My Programs', link: '/trainer/programs' }
    ]
  },
  {
    keywords: ['student', 'students', 'manage students', 'view students', 'student list', 'my students'],
    question: 'How do I manage my students?',
    answer: `Managing your students is easy! Here's what you can do:

👥 **View All Students** - Go to "My Students" to see everyone enrolled in your programs
📊 **Track Progress** - Monitor each student's attendance, progress, and engagement
💬 **Communicate** - Message students directly through the messaging system
📅 **View Attendance** - Check attendance records and session history
📈 **Analytics** - See performance metrics for each student

You can access all student management features from the "My Students" section in your dashboard. Want to see a specific student's details?`,
    quickActions: [
      { text: 'My Students', link: '/trainer/students' },
      { text: 'Dashboard', link: '/trainer/home' }
    ]
  },
  {
    keywords: ['class', 'classes', 'schedule', 'create class', 'add class', 'class management', 'session'],
    question: 'How do I schedule classes?',
    answer: `Scheduling classes is simple! Here's the process:

1. **Go to Classes** - Navigate to the "Classes" section in your dashboard
2. **Create New Class** - Click "Add Class" or "Schedule Class"
3. **Set Details** - Choose the date, time, duration, and program
4. **Select Students** - Choose which students should attend (or make it open)
5. **Save** - Your class will be scheduled and students will be notified!

You can schedule:
• **One-on-one sessions** - Individual training sessions
• **Group classes** - Multiple students at once
• **Recurring classes** - Set up regular weekly/bi-weekly sessions

Students will see upcoming classes in their dashboard and can mark attendance. Need help scheduling a specific type of class?`,
    quickActions: [
      { text: 'Go to Classes', link: '/trainer/classes' },
      { text: 'Create Class', link: '/trainer/classes' }
    ]
  },
  {
    keywords: ['fee', 'fees', 'payment', 'payments', 'pricing', 'price', 'fee plan', 'payment plan', 'revenue', 'earnings'],
    question: 'How do I set up fee plans?',
    answer: `Setting up fee plans helps you manage your pricing effectively! Here's how:

💰 **Create Fee Plans** - Go to "Fee Plans" in your dashboard
📋 **Set Pricing** - Define different payment structures:
   • One-time payments
   • Monthly subscriptions
   • Per-session rates
   • Package deals
💳 **Payment Tracking** - Monitor which students have paid and which payments are pending
📊 **Revenue Overview** - View your monthly earnings and payment history

You can create multiple fee plans for different programs or services. This makes it easy for students to choose what works for them while you track all payments in one place. Want to see your current fee plans?`,
    quickActions: [
      { text: 'Fee Plans', link: '/trainer/fee-plan' },
      { text: 'Payment Details', link: '/trainer/fees' }
    ]
  },
  {
    keywords: ['payment', 'payments', 'pending', 'received', 'collect', 'revenue', 'earnings', 'income'],
    question: 'How do I track payments?',
    answer: `Tracking payments is essential for your business! Here's what you can do:

💵 **Payment Dashboard** - View all payments in the "Fees" section
📊 **Payment Status** - See which payments are:
   • Completed ✅
   • Pending ⏳
   • Overdue ⚠️
📈 **Revenue Stats** - Check your monthly revenue on the dashboard
📋 **Payment History** - View detailed payment records for each student
💰 **Generate Invoices** - Create and download invoices for your records

You can see payment summaries on your main dashboard and detailed information in the Fees section. This helps you stay on top of your earnings and follow up on any pending payments. Need to check a specific payment?`,
    quickActions: [
      { text: 'View Fees', link: '/trainer/fees' },
      { text: 'Dashboard Stats', link: '/trainer/home' }
    ]
  },
  {
    keywords: ['dashboard', 'stats', 'statistics', 'overview', 'performance', 'analytics', 'metrics'],
    question: 'What can I see on my dashboard?',
    answer: `Your Professional Dashboard gives you a complete overview of your business! Here's what you'll see:

📊 **Key Metrics**:
   • Total Students enrolled
   • Active Programs you're offering
   • Monthly Revenue earned
   • Completion Rate of programs
   • Upcoming Classes scheduled
   • Pending Payments to collect

🎯 **Monthly Teaching Goal** - Track your progress toward your monthly session target
👥 **Recent Students** - Quick view of your active students
📅 **Upcoming Classes** - See what's scheduled next
💰 **Recent Activity** - Payment and enrollment updates

The dashboard updates in real-time, so you always have the latest information about your business. It's your command center for managing everything! Want to explore a specific section?`,
    quickActions: [
      { text: 'View Dashboard', link: '/trainer/home' },
      { text: 'My Students', link: '/trainer/students' },
      { text: 'My Programs', link: '/trainer/programs' }
    ]
  },
  {
    keywords: ['notification', 'notifications', 'alert', 'alerts', 'message', 'messages', 'inbox'],
    question: 'How do notifications work?',
    answer: `Notifications keep you informed about important updates! Here's what you'll receive:

🔔 **Student Messages** - When students message you
📅 **Class Reminders** - Upcoming classes and sessions
💰 **Payment Updates** - When payments are received or pending
👥 **New Enrollments** - When students join your programs
📊 **Dashboard Updates** - Important business metrics

You can view all notifications in the "Notifications" section of your dashboard. Make sure to check regularly so you don't miss any important updates from your students!

You can also set up availability notifications to let students know when you're available for new programs. Want to check your notifications?`,
    quickActions: [
      { text: 'View Notifications', link: '/trainer/notifications' },
      { text: 'Dashboard', link: '/trainer/home' }
    ]
  },
  {
    keywords: ['profile', 'edit profile', 'update profile', 'settings', 'account', 'personal information'],
    question: 'How do I update my profile?',
    answer: `Keeping your profile updated helps students find and trust you! Here's how:

1. **Go to Profile** - Click on your profile icon or navigate to "Profile" in settings
2. **Edit Information** - Update your:
   • Name and contact details
   • Profile picture
   • Bio and description
   • Specializations and certifications
   • Service types you offer
3. **Save Changes** - Your updated profile will be visible to students

A complete and professional profile helps students understand your expertise and choose you as their trainer. Make sure to include your qualifications and what makes you unique! Want to update your profile now?`,
    quickActions: [
      { text: 'My Profile', link: '/trainer/profile' },
      { text: 'Settings', link: '/trainer/settings' }
    ]
  },
  {
    keywords: ['help', 'support', 'contact', 'assistance', 'issue', 'problem', 'question'],
    question: 'How can I get help?',
    answer: `I'm here to help you succeed! You can get assistance in several ways:

💬 **Right here** - I can answer your questions about using the platform
📱 **WhatsApp Support** - Park your message and get a reply soon within official hours
📧 **Email Support** - Contact our support team for detailed assistance
📚 **Dashboard Guide** - Explore your dashboard to discover all features

For technical issues, account problems, or business inquiries, our support team is ready to help. Is there something specific you need help with?`,
    quickActions: [
      { text: 'Contact Support', action: 'whatsapp' },
      { text: 'View Dashboard', link: '/trainer/home' }
    ]
  },
  {
    keywords: ['switch', 'member', 'member view', 'student view', 'toggle', 'dashboard view'],
    question: 'How do I switch to member view?',
    answer: `You can easily switch between your Professional and Member dashboards! Here's how:

🔄 **Toggle Dashboard** - Look for the "Switch to Member View" option in your Professional Dashboard
👤 **Member View** - This shows you the student experience, where you can:
   • Browse programs as a student
   • Enroll in other trainers' programs
   • Track your own learning progress
   • Access member features

This is useful if you want to experience the platform from a student's perspective or if you're also taking classes yourself. You can switch back to Professional view anytime! Want to switch now?`,
    quickActions: [
      { text: 'Switch to Member View', action: 'switch-member' },
      { text: 'Stay Here', action: 'cancel' }
    ]
  }
];

// Greeting message for trainer chatbot
const trainerGreeting = "Hello! I'm Ananya, your Professional Assistant. I'm here to help you manage your training business on Yogvital. How can I assist you today? 🎯";

const TrainerChatBot = () => {
  const { session } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isPWA, setIsPWA] = useState(false);
  const [messages, setMessages] = useState(() => {
    // Load messages from sessionStorage if available
    try {
      const savedMessages = sessionStorage.getItem('trainer-chatbot-messages');
      return savedMessages ? JSON.parse(savedMessages) : [];
    } catch {
      return [];
    }
  });
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inactivityTimerRef = useRef(null);
  const lastInteractionRef = useRef(null);

  // Detect PWA mode
  useEffect(() => {
    const checkPWA = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
      setIsPWA(standalone);
    };
    checkPWA();
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleChange = () => checkPWA();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Start inactivity timer
  const startInactivityTimer = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    inactivityTimerRef.current = setTimeout(() => {
      const timeSinceLastInteraction = Date.now() - (lastInteractionRef.current || Date.now());
      if (timeSinceLastInteraction >= 60000 && messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage && lastMessage.text !== "Is there anything else I can help you with? 😊") {
          addMessage('bot', "Is there anything else I can help you with? 😊", [
            'Contact Support',
            'No, thanks!'
          ]);
        }
      }
    }, 60000);
  };

  const resetInactivityTimer = () => {
    lastInteractionRef.current = Date.now();
    startInactivityTimer();
  };

  // Initialize with greeting
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addMessage('bot', trainerGreeting);
      lastInteractionRef.current = Date.now();
      startInactivityTimer();
    } else if (isOpen && messages.length > 0) {
      resetInactivityTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, messages.length]);

  // Auto-scroll to bottom
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const addMessage = (sender, text, quickActions = null) => {
    setMessages(prev => {
      const newMessages = [...prev, { sender, text, quickActions, timestamp: new Date() }];
      try {
        sessionStorage.setItem('trainer-chatbot-messages', JSON.stringify(newMessages));
      } catch {
        // Ignore storage errors
      }
      return newMessages;
    });
  };

  const findMatchingFAQ = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    for (const faq of trainerFaqData) {
      for (const keyword of faq.keywords) {
        if (lowerMessage.includes(keyword.toLowerCase())) {
          return faq;
        }
      }
    }
    return null;
  };

  const handleQuickAction = (action) => {
    if (typeof action === 'string') {
      if (action === 'whatsapp') {
        const whatsappMessage = encodeURIComponent("Hi! I need more help with Yogvital as a trainer.");
        window.open(`https://wa.me/919876543210?text=${whatsappMessage}`, '_blank');
        addMessage('bot', "I've opened WhatsApp for you. Our support team will get back to you soon within official hours! 💚");
      } else if (action === 'switch-member') {
        navigate('/student/home');
        addMessage('bot', "Switching you to Member Dashboard view... 👤");
      } else if (action === 'cancel') {
        addMessage('bot', "No problem! Is there anything else I can help you with? 😊");
      } else if (action === 'program') {
        addMessage('user', 'How do I create a program?');
        const faq = trainerFaqData.find(f => f.keywords.includes('program'));
        if (faq) {
          setTimeout(() => {
            addMessage('bot', faq.answer, faq.quickActions);
          }, 500);
        }
      } else if (action === 'student') {
        addMessage('user', 'How do I manage my students?');
        const faq = trainerFaqData.find(f => f.keywords.includes('student'));
        if (faq) {
          setTimeout(() => {
            addMessage('bot', faq.answer, faq.quickActions);
          }, 500);
        }
      } else if (action === 'class') {
        addMessage('user', 'How do I schedule classes?');
        const faq = trainerFaqData.find(f => f.keywords.includes('class'));
        if (faq) {
          setTimeout(() => {
            addMessage('bot', faq.answer, faq.quickActions);
          }, 500);
        }
      } else if (action === 'payment') {
        addMessage('user', 'How do I track payments?');
        const faq = trainerFaqData.find(f => f.keywords.includes('payment'));
        if (faq) {
          setTimeout(() => {
            addMessage('bot', faq.answer, faq.quickActions);
          }, 500);
        }
      } else if (action === 'dashboard') {
        addMessage('user', 'What can I see on my dashboard?');
        const faq = trainerFaqData.find(f => f.keywords.includes('dashboard'));
        if (faq) {
          setTimeout(() => {
            addMessage('bot', faq.answer, faq.quickActions);
          }, 500);
        }
      } else if (action === 'retry') {
        addMessage('bot', "Sure! What would you like to know? I can help you with programs, students, classes, payments, and more! 😊");
      } else {
        addMessage('bot', "I'm processing that for you...");
      }
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isTyping) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    addMessage('user', userMessage);
    resetInactivityTimer();

    // Simulate typing delay for more human-like interaction
    setIsTyping(true);
    
    setTimeout(() => {
      setIsTyping(false);
      
      const lowerMessage = userMessage.toLowerCase().trim();
      
      // Check for greetings first
      const greetingPatterns = [
        'hi', 'hello', 'hey', 'hola', 'namaste', 'namaskar',
        'good morning', 'good afternoon', 'good evening', 'good night',
        'morning', 'afternoon', 'evening', 'night',
        'howdy', 'greetings', 'sup', 'what\'s up', 'whats up',
        'hi there', 'hello there', 'hey there'
      ];
      
      const isGreeting = greetingPatterns.some(pattern => 
        lowerMessage === pattern || 
        lowerMessage.startsWith(pattern + ' ') || 
        lowerMessage === pattern + '!'
      );
      
      if (isGreeting) {
        setTimeout(() => {
          addMessage('bot', `Hello! Great to hear from you! 👋 I'm here to help you manage your training business on Yogvital. What would you like to know today?`, [
            { text: 'How to create programs?', action: 'program' },
            { text: 'Manage my students', action: 'student' },
            { text: 'Schedule classes', action: 'class' },
            { text: 'Track payments', action: 'payment' },
            { text: 'View dashboard stats', action: 'dashboard' }
          ]);
        }, 500);
        return;
      }
      
      // Check for "how are you" or similar
      if (lowerMessage.includes('how are you') || lowerMessage.includes('how\'s it going') || lowerMessage.includes('how do you do')) {
        setTimeout(() => {
          addMessage('bot', `I'm doing great, thank you for asking! 😊 I'm here and ready to help you with your training business. What can I assist you with today?`, [
            { text: 'Create a program', link: '/trainer/programs' },
            { text: 'View my students', link: '/trainer/students' },
            { text: 'Check dashboard', link: '/trainer/home' }
          ]);
        }, 500);
        return;
      }
      
      // Check for "thanks" or "thank you"
      if (lowerMessage.includes('thank') || lowerMessage.includes('thanks')) {
        setTimeout(() => {
          addMessage('bot', `You're very welcome! 😊 I'm always here to help. Is there anything else you'd like to know about managing your business on Yogvital?`);
        }, 500);
        return;
      }
      
      // Find matching FAQ
      const matchedFAQ = findMatchingFAQ(userMessage);
      
      if (matchedFAQ) {
        // Add delay to simulate thinking
        setTimeout(() => {
          setIsTyping(false);
          addMessage('bot', matchedFAQ.answer, matchedFAQ.quickActions);
        }, 500);
      } else {
        // AI Response for "vigorous queries"
        (async () => {
          try {
            const response = await HttpInterceptor.post('/api/ai/general-chat', {
              message: userMessage,
              history: messages.slice(-6),
              type: 'trainer'
            });

            if (response.data?.success) {
              addMessage('bot', response.data.data.text);
            } else {
              throw new Error('AI response failed');
            }
          } catch (error) {
            console.error("AI Chat Error:", error);
            addMessage('bot', `Hmm, this query is a bit outside my current scope of knowledge. Let me connect you with our support team who can provide more detailed assistance! 😊`, [
              { text: 'Contact Support', action: 'whatsapp' },
              { text: 'Try Another Question', action: 'retry' }
            ]);
          } finally {
            setIsTyping(false);
          }
        })();
      }
    }, 800);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Load messages from sessionStorage when opening
      try {
        const savedMessages = sessionStorage.getItem('trainer-chatbot-messages');
        if (savedMessages) {
          const parsed = JSON.parse(savedMessages);
          setMessages(parsed);
        }
      } catch {
        // Ignore errors
      }
    }
  };

  const refreshChat = () => {
    setMessages([]);
    sessionStorage.removeItem('trainer-chatbot-messages');
    if (isOpen) {
      addMessage('bot', trainerGreeting);
      lastInteractionRef.current = Date.now();
      startInactivityTimer();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Don't render if user is explicitly not a trainer (but allow rendering while session is loading)
  // Only hide if we have a session and it's confirmed the user is not a trainer
  if (session && !session.isTrainer) {
    return null;
  }
  
  // If no session yet, don't render (will render once session loads)
  if (!session) {
    return null;
  }

  // Hide TrainerChatBot when trainer is viewing member dashboard (they should see StudentChatBot instead)
  // Only show TrainerChatBot when on trainer routes
  const isOnTrainerDashboard = location.pathname.startsWith('/trainer');
  if (!isOnTrainerDashboard) {
    return null;
  }

  return (
    <>
      {/* Chat Button - Always visible when chat is closed */}
      <div 
        className={`fixed ${isPWA ? 'bottom-4' : 'bottom-10'} right-4 z-[99999] ${isOpen ? 'hidden' : 'block'}`}
        style={{ position: 'fixed', zIndex: 99999 }}
      >
        <button
          onClick={toggleChat}
          className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 ring-4 ring-white/30 relative"
          aria-label="Open trainer assistant chat"
        >
          <FaChalkboardTeacher className="text-xl sm:text-2xl" />
          <span className="absolute top-0 right-0 w-3 h-3 sm:w-4 sm:h-4 bg-green-500 rounded-full border-2 border-white animate-pulse"></span>
        </button>
      </div>
      
      {/* Close Button - Only visible when chat is open */}
      {isOpen && (
        <div 
          className={`fixed ${isPWA ? 'bottom-4' : 'bottom-10'} right-4 z-[99999]`}
          style={{ position: 'fixed', zIndex: 99999 }}
        >
          <button
            onClick={toggleChat}
            className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 text-white p-3 sm:p-4 rounded-full shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-110 ring-4 ring-white/30"
            aria-label="Close trainer assistant chat"
          >
            <FaTimes className="text-lg sm:text-xl" />
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed ${isPWA ? 'bottom-20' : 'bottom-24'} right-4 w-[calc(100vw-2rem)] sm:w-80 md:w-96 max-w-[384px] bg-white rounded-2xl shadow-2xl z-[99999] flex flex-col max-h-[600px] ring-4 ring-white/30`}
          style={{ position: 'fixed', zIndex: 99999 }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              {avatarError ? (
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold">
                  A
                </div>
              ) : (
                <img
                  src={ananyaAvatar}
                  alt="Ananya"
                  className="w-10 h-10 rounded-full border-2 border-white/50"
                  onError={() => setAvatarError(true)}
                />
              )}
              <div>
                <p className="font-semibold">Ananya</p>
                <p className="text-xs text-white/90">Professional Assistant • Ready to help</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={refreshChat}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Refresh chat"
                title="Refresh chat"
              >
                <FaSync className="text-sm" />
              </button>
              <button
                onClick={toggleChat}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
                aria-label="Close chat"
              >
                <FaTimes />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
            style={{ maxHeight: '400px' }}
          >
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start gap-2 max-w-[80%] ${msg.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  {msg.sender === 'bot' && (
                    <div className="flex-shrink-0">
                      {avatarError ? (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-sm font-bold text-white">
                          A
                        </div>
                      ) : (
                        <img
                          src={ananyaAvatar}
                          alt="Ananya"
                          className="w-8 h-8 rounded-full border border-orange-200"
                          onError={() => setAvatarError(true)}
                        />
                      )}
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-2 ${
                      msg.sender === 'user'
                        ? 'bg-gradient-to-r from-orange-600 to-amber-600 text-white'
                        : 'bg-white text-gray-800 shadow-sm border border-gray-200'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                    {msg.quickActions && (
                      <div className="mt-3 space-y-2">
                        {msg.quickActions.map((action, actionIdx) => {
                          if (typeof action === 'string') {
                            return (
                              <button
                                key={actionIdx}
                                onClick={() => handleQuickAction(action)}
                                className="block w-full text-left px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              >
                                {action}
                              </button>
                            );
                          } else if (action.link) {
                            return (
                              <Link
                                key={actionIdx}
                                to={action.link}
                                onClick={() => {
                                  const element = document.getElementById(action.link.split('#')[1]);
                                  if (element) {
                                    element.scrollIntoView({ behavior: 'smooth' });
                                  }
                                }}
                                className="block w-full text-left px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              >
                                {action.text}
                              </Link>
                            );
                          } else if (action.action) {
                            return (
                              <button
                                key={actionIdx}
                                onClick={() => handleQuickAction(action.action)}
                                className="block w-full text-left px-3 py-2 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                              >
                                {action.text}
                              </button>
                            );
                          }
                          return null;
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0">
                  {avatarError ? (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-sm font-bold text-white">
                      A
                    </div>
                  ) : (
                    <img
                      src={ananyaAvatar}
                      alt="Ananya"
                      className="w-8 h-8 rounded-full border border-orange-200"
                      onError={() => setAvatarError(true)}
                    />
                  )}
                </div>
                <div className="bg-white rounded-2xl px-4 py-2 shadow-sm border border-gray-200">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white rounded-b-2xl">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isTyping}
                className="p-2 bg-gradient-to-r from-orange-600 to-amber-600 text-white rounded-lg hover:from-orange-700 hover:to-amber-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Send message"
              >
                <FaPaperPlane />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TrainerChatBot;

